import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Circle, Loader2, Minus } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useYouTubeAccountReset } from '@/lib/youtubeConnection';
import { YouTubeDisconnectButton } from '@/components/channel/YouTubeDisconnectButton';
import { formatApiError } from '@/lib/formatApiError';
import { autofillFromUploadedVideo, watchDiscoverySeo } from '@/lib/publishVideoAutofill';
import { uploadFileViaPresignedUrl } from '@/lib/exportMediaStaging';

const DRAFT_KEY = 'twinnee_auto_seo_draft';

const STEPS = [
  { id: 'connect', label: 'Connect YouTube' },
  { id: 'video', label: 'Choose the video' },
  { id: 'transcribe', label: 'Transcribe the video' },
  { id: 'package', label: 'Package title, description, and tags' },
  { id: 'thumbnail', label: 'Pick a thumbnail' },
  { id: 'audit', label: 'Pre-publish audit' },
  { id: 'legal', label: 'Confirm compliance' },
  { id: 'upload', label: 'Upload unlisted' },
  { id: 'after', label: 'Studio remainders' },
  { id: 'handoff', label: 'Watch ranking' },
];

const EMPTY_STATUS = Object.fromEntries(STEPS.map((step) => [step.id, 'queued']));

function loadDraft() {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function withHashtags(description, tags) {
  const text = String(description || '');
  const found = text.match(/#[A-Za-z0-9_]+/g) || [];
  if (found.length >= 3) return text;
  const seen = new Set(found.map((tag) => tag.slice(1).toLowerCase()));
  const extras = [];
  for (const tag of tags || []) {
    const slug = String(tag).replace(/[^A-Za-z0-9]+/g, '');
    if (!slug || seen.has(slug.toLowerCase())) continue;
    seen.add(slug.toLowerCase());
    extras.push(`#${slug}`);
    if (found.length + extras.length >= 5) break;
  }
  if (extras.length < 3 && found.length + extras.length < 3) return text;
  const needed = extras.slice(0, Math.max(0, 5 - found.length));
  if (!needed.length) return text;
  return `${text.trim()}\n\n${needed.join(' ')}`;
}

function taskVideoUrl(task) {
  return String(task?.export_video_url || task?.final_video_url || task?.video_url || '').trim();
}

function youtubeVideoId(result) {
  const direct = String(result?.video_id || '').trim();
  if (direct) return direct;
  const url = String(result?.url || result?.shorts_url || '');
  const match = url.match(/(?:v=|shorts\/|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  return match ? match[1] : '';
}

function storedAccountPaid() {
  try {
    const stored = JSON.parse(localStorage.getItem('storycraft_user') || '{}');
    return Boolean(stored.advanced_analytics || stored.account_paid);
  } catch {
    return false;
  }
}

function isShortsTask(task) {
  const aspect = String(task?.aspect_ratio || task?.aspectRatio || '');
  return aspect.includes('9:16') || aspect.includes('9 / 16');
}

function StepIcon({ status }) {
  if (status === 'running') return <Loader2 className="w-4 h-4 animate-spin text-[#67e8f9]" />;
  if (status === 'done') return <Check className="w-4 h-4 text-[#06211b]" />;
  if (status === 'skipped') return <Minus className="w-4 h-4 text-[#8b95a7]" />;
  if (status === 'failed' || status === 'needs_you') return <Circle className="w-4 h-4 text-[#d6ff4a]" />;
  return <Circle className="w-4 h-4 text-[#5a6475]" />;
}

export function AutoSeoPublishPage() {
  const navigate = useNavigate();
  const saved = useMemo(() => loadDraft(), []);
  const queryVideo = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get('video')?.trim() || '';
    } catch {
      return '';
    }
  }, []);
  const [statuses, setStatuses] = useState(() => {
    const base = { ...EMPTY_STATUS, ...(saved?.statuses || {}) };
    if (queryVideo && base.video === 'queued') base.video = 'done';
    return base;
  });
  const [active, setActive] = useState(saved?.active || 'connect');
  const [running, setRunning] = useState(false);
  const [yt, setYt] = useState(null);
  const [projects, setProjects] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [videoUrl, setVideoUrl] = useState(queryVideo || saved?.videoUrl || '');
  const [title, setTitle] = useState(saved?.title || '');
  const [description, setDescription] = useState(saved?.description || '');
  const [tags, setTags] = useState(saved?.tags || []);
  const [categoryId, setCategoryId] = useState(saved?.categoryId || '22');
  const [language, setLanguage] = useState(saved?.language || 'en');
  const [thumbnailUrl, setThumbnailUrl] = useState(saved?.thumbnailUrl || '');
  const [thumbnailText, setThumbnailText] = useState(saved?.thumbnailText || '');
  const [thumbOptions, setThumbOptions] = useState(saved?.thumbOptions || []);
  const [transcript, setTranscript] = useState(saved?.transcript || '');
  const [shorts, setShorts] = useState(Boolean(saved?.shorts));
  const [madeForKids, setMadeForKids] = useState(saved?.madeForKids ?? null);
  const [paidPromotion, setPaidPromotion] = useState(saved?.paidPromotion ?? null);
  const [alteredContent, setAlteredContent] = useState(saved?.alteredContent ?? null);
  const [fromTwinnee, setFromTwinnee] = useState(Boolean(saved?.fromTwinnee));
  const [pinnedComment, setPinnedComment] = useState(saved?.pinnedComment || '');
  const [playlistId, setPlaylistId] = useState(saved?.playlistId || '');
  const [publishPublic, setPublishPublic] = useState(Boolean(saved?.publishPublic));
  const [hasSrt, setHasSrt] = useState(Boolean(saved?.hasSrt));
  const [variants, setVariants] = useState(saved?.variants || []);
  const [audit, setAudit] = useState(saved?.audit || null);
  const [uploadResult, setUploadResult] = useState(saved?.uploadResult || null);
  const [narrationProjectId, setNarrationProjectId] = useState(saved?.narrationProjectId || null);
  const [storyId, setStoryId] = useState(saved?.storyId || null);
  const [note, setNote] = useState('');
  const [rankingUnlocked, setRankingUnlocked] = useState(() => Boolean(saved?.audit?.unlocked) || storedAccountPaid());
  const stepRef = useRef(saved?.active || 'connect');

  const legalReady = madeForKids !== null && paidPromotion !== null && alteredContent !== null;
  const blocking = Array.isArray(audit?.blocking) ? audit.blocking : [];

  useEffect(() => {
    if (!legalReady) return undefined;
    setStatuses((prev) => (prev.legal === 'done' ? prev : { ...prev, legal: 'done' }));
    if (!title.trim()) return undefined;
    let cancelled = false;
    runAudit().catch(() => {
      if (!cancelled) return null;
    });
    return () => {
      cancelled = true;
    };
    // Score again once all three legal answers exist.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legalReady]);

  useEffect(() => {
    const draft = {
      statuses, active, videoUrl, title, description, tags, categoryId, language,
      thumbnailUrl, thumbnailText, thumbOptions, transcript, shorts, madeForKids,
      paidPromotion, alteredContent, fromTwinnee, pinnedComment, playlistId,
      publishPublic, hasSrt, variants, audit, uploadResult, narrationProjectId, storyId,
    };
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch {
      /* ignore quota */
    }
  }, [
    statuses, active, videoUrl, title, description, tags, categoryId, language,
    thumbnailUrl, thumbnailText, thumbOptions, transcript, shorts, madeForKids,
    paidPromotion, alteredContent, fromTwinnee, pinnedComment, playlistId,
    publishPublic, hasSrt, variants, audit, uploadResult, narrationProjectId, storyId,
  ]);

  const mark = useCallback((id, status) => {
    stepRef.current = id;
    setStatuses((prev) => ({ ...prev, [id]: status }));
    setActive(id);
  }, []);

  const refreshConnection = useCallback(async () => {
    const { data } = await api.get('/social-publish/youtube/status');
    setYt(data);
    if (data?.connected && data?.scopes_ok !== false) {
      try {
        const { data: lists } = await api.get('/social-publish/youtube/playlists');
        setPlaylists(Array.isArray(lists?.items) ? lists.items : []);
      } catch {
        setPlaylists([]);
      }
    }
    return data;
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('youtube') === 'connected') toast.success('YouTube connected.');
    if (params.get('youtube') === 'error') toast.error('YouTube reconnect did not finish.');
    if (params.has('youtube')) {
      params.delete('youtube');
      params.delete('message');
      const next = params.toString();
      window.history.replaceState({}, '', next ? `/social-publish/auto?${next}` : '/social-publish/auto');
    }
    refreshConnection().catch(() => setYt({ connected: false }));
    api.get('/billing/wallet')
      .then(({ data }) => setRankingUnlocked(Boolean(data?.advanced_analytics || data?.paid)))
      .catch(() => {});
    api.get('/dashboard/tasks')
      .then(({ data }) => setProjects(Array.isArray(data?.tasks) ? data.tasks : []))
      .catch(() => setProjects([]));
  }, [refreshConnection]);

  useYouTubeAccountReset(useCallback(() => {
    setYt({ connected: false, channel_title: null });
    refreshConnection().catch(() => setYt({ connected: false, channel_title: null }));
  }, [refreshConnection]));

  const runAudit = useCallback(async (overrides = {}) => {
    const body = {
      title: overrides.title ?? title,
      description: overrides.description ?? description,
      tags: overrides.tags ?? tags,
      thumbnail_text: overrides.thumbnailText ?? thumbnailText,
      has_custom_thumbnail: Boolean(overrides.thumbnailUrl ?? thumbnailUrl),
      default_language: overrides.language ?? language,
      category_id: overrides.categoryId ?? categoryId,
      in_playlist: Boolean(overrides.playlistId ?? playlistId),
      privacy_status: (overrides.publishPublic ?? publishPublic) ? 'public' : 'unlisted',
      license: 'youtube',
      embeddable: true,
      made_for_kids: overrides.madeForKids !== undefined ? overrides.madeForKids : madeForKids,
      paid_promotion: overrides.paidPromotion !== undefined ? overrides.paidPromotion : paidPromotion,
      altered_content: overrides.alteredContent !== undefined ? overrides.alteredContent : alteredContent,
      has_srt: overrides.hasSrt ?? hasSrt,
      transcript: overrides.transcript ?? transcript,
      pinned_comment: overrides.pinnedComment ?? pinnedComment,
      optimize_for_shorts: overrides.shorts ?? shorts,
    };
    const { data } = await api.post('/channel/prepublish-audit', body);
    setAudit(data);
    if (data?.unlocked) setRankingUnlocked(true);
    if (Array.isArray(data?.title_variants)) setVariants(data.title_variants);
    if (!thumbnailText && data?.thumbnail_text_suggestion) setThumbnailText(data.thumbnail_text_suggestion);
    if (!pinnedComment && data?.pinned_comment_suggestion) setPinnedComment(data.pinned_comment_suggestion);
    return data;
  }, [
    title, description, tags, thumbnailText, thumbnailUrl, language, categoryId,
    playlistId, publishPublic, madeForKids, paidPromotion, alteredContent, hasSrt,
    transcript, pinnedComment, shorts,
  ]);

  const connectStep = async () => {
    mark('connect', 'running');
    const data = await refreshConnection();
    if (!data?.connected || data?.scopes_ok === false) {
      mark('connect', 'needs_you');
      setNote(data?.connected ? 'Reconnect YouTube and approve every permission.' : 'Connect YouTube to continue.');
      return false;
    }
    mark('connect', 'done');
    return true;
  };

  const packageFrom = async ({ hint, script, nextLanguage, nextShorts }) => {
    mark('package', 'running');
    const { data } = await api.post('/social-publish/youtube/suggest-metadata', {
      title_hint: hint || title,
      script_text: script || transcript,
      language: nextLanguage || language || 'en',
      optimize_for_shorts: nextShorts ?? shorts,
    });
    const nextTags = Array.isArray(data.tags) ? data.tags : [];
    const nextDescription = withHashtags(data.description || '', nextTags);
    setTitle(data.title || title);
    setDescription(nextDescription);
    setTags(nextTags);
    if (data.category_id) setCategoryId(String(data.category_id));
    if (data.default_language) setLanguage(data.default_language);
    mark('package', 'done');
    return {
      title: data.title || title,
      description: nextDescription,
      tags: nextTags,
      categoryId: String(data.category_id || categoryId),
      language: data.default_language || language,
    };
  };

  const thumbnailStep = async (pack) => {
    mark('thumbnail', 'running');
    try {
      const { data } = await api.post('/social-publish/ai-thumbnail-options', {
        platform: 'youtube',
        title: (pack?.title || title || 'Video').slice(0, 120),
        info_line: (pack?.description || description || '').split('\n')[0]?.slice(0, 180) || '',
        script_excerpt: (transcript || '').slice(0, 2000),
        count: 3,
      });
      const urls = Array.isArray(data?.urls) ? data.urls.filter(Boolean) : [];
      if (urls.length) {
        setThumbOptions(urls);
        setThumbnailUrl(urls[0]);
        mark('thumbnail', 'done');
        return urls[0];
      }
    } catch {
      /* fall through to frames */
    }
    if (videoUrl) {
      try {
        const { data } = await api.post('/social-publish/youtube/thumbnail-frame-options', { video_url: videoUrl });
        const urls = Array.isArray(data?.urls) ? data.urls.filter(Boolean) : [];
        if (urls[0]) {
          setThumbOptions(urls);
          setThumbnailUrl(urls[0]);
          mark('thumbnail', 'done');
          return urls[0];
        }
      } catch {
        /* continue without a thumbnail */
      }
    }
    mark('thumbnail', thumbnailUrl ? 'done' : 'needs_you');
    setNote('Thumbnail generation failed. Upload one, or continue and the audit will flag it.');
    return thumbnailUrl;
  };

  const auditStep = async (overrides = {}) => {
    mark('audit', 'running');
    const data = await runAudit(overrides);
    mark('audit', 'done');
    return data;
  };

  const resume = async ({ fromPackage = false } = {}) => {
    if (running) return;
    setRunning(true);
    setNote('');
    try {
      const connected = await connectStep();
      if (!connected) return;
      if (!videoUrl && !title.trim()) {
        mark('video', 'needs_you');
        setNote('Pick an exported project or upload a video.');
        return;
      }
      mark('video', 'done');

      let script = transcript;
      let hint = title;
      let nextLanguage = language;
      let packaged = null;

      if (!fromPackage && videoUrl && !transcript) {
        mark('transcribe', 'running');
        setNote('Transcribing…');
        try {
          const result = await autofillFromUploadedVideo(
            {
              video_url: videoUrl,
              language: language || 'en',
              optimize_for_shorts: shorts,
              platform: 'youtube',
            },
            { onProgress: (row) => setNote(row?.message || 'Transcribing…') },
          );
          script = result.transcript || '';
          hint = result.title || hint;
          nextLanguage = result.default_language || nextLanguage;
          setTranscript(script);
          if (result.primary_keyword) hint = result.title || hint;
          const nextTags = Array.isArray(result.tags) ? result.tags : tags;
          const nextDescription = withHashtags(result.description || description, nextTags);
          if (result.title) setTitle(result.title);
          if (nextDescription) setDescription(nextDescription);
          if (nextTags.length) setTags(nextTags);
          const baselineTitle = result.title || '';
          const baselineDescription = nextDescription;
          const baselineTags = nextTags.join('\u0000');
          watchDiscoverySeo(videoUrl, {
            onPack: (seo) => {
              if (seo.title) setTitle((current) => (current === baselineTitle ? seo.title : current));
              if (seo.description) setDescription((current) => (current === baselineDescription ? seo.description : current));
              if (Array.isArray(seo.tags) && seo.tags.length) {
                setTags((current) => (current.join('\u0000') === baselineTags ? seo.tags : current));
              }
            },
          });
          if (result.category_id) setCategoryId(String(result.category_id));
          if (result.default_language) setLanguage(result.default_language);
          mark('transcribe', 'done');
          mark('package', 'done');
          packaged = {
            title: result.title || title,
            description: nextDescription,
            tags: nextTags,
            categoryId: String(result.category_id || categoryId),
            language: result.default_language || language,
          };
        } catch (err) {
          if (err?.cancelled) return;
          mark('transcribe', 'skipped');
          setNote('Transcription did not finish. Packaging from the title instead.');
          packaged = await packageFrom({ hint, script: '', nextLanguage, nextShorts: shorts });
        }
      } else if (!transcript && !title.trim()) {
        mark('transcribe', 'skipped');
      } else if (fromPackage || !description) {
        if (!transcript) mark('transcribe', videoUrl ? statuses.transcribe : 'skipped');
        packaged = await packageFrom({ hint, script, nextLanguage, nextShorts: shorts });
      } else {
        mark('transcribe', transcript ? 'done' : 'skipped');
        mark('package', 'done');
        packaged = { title, description, tags, categoryId, language };
      }

      const thumb = await thumbnailStep(packaged);
      await auditStep({
        ...(packaged || {}),
        thumbnailUrl: thumb,
        transcript: script,
      });
      mark('legal', legalReady ? 'done' : 'needs_you');
      if (!legalReady) setNote('We cannot legally choose Made for Kids, paid promotion, or AI disclosure.');
    } catch (err) {
      toast.error(formatApiError(err, 'Automate All stopped'));
      const failedStep = stepRef.current;
      setStatuses((prev) => ({
        ...prev,
        [failedStep]: prev[failedStep] === 'running' ? 'failed' : prev[failedStep],
      }));
    } finally {
      setRunning(false);
    }
  };

  const publish = async () => {
    if (!legalReady) {
      mark('legal', 'needs_you');
      toast.error('Answer Made for Kids, paid promotion, and altered content first.');
      return;
    }
    if (!videoUrl || !title.trim()) {
      toast.error('A video and title are required.');
      return;
    }
    setRunning(true);
    mark('upload', 'running');
    try {
      const fresh = await runAudit();
      if (Array.isArray(fresh?.blocking) && fresh.blocking.length) {
        setStatuses((prev) => ({ ...prev, upload: 'queued', legal: 'needs_you' }));
        setActive('legal');
        toast.error('Answer Made for Kids, paid promotion, and altered content first.');
        return;
      }
      const { data } = await api.post('/social-publish/youtube/upload', {
        video_url: videoUrl,
        title: title.trim(),
        description: description.trim(),
        tags,
        category_id: categoryId || '22',
        privacy_status: publishPublic ? 'public' : 'unlisted',
        thumbnail_url: thumbnailUrl || null,
        default_language: language || null,
        default_audio_language: language || null,
        playlist_id: playlistId || null,
        notify_subscribers: false,
        embeddable: true,
        license: 'youtube',
        made_for_kids: Boolean(madeForKids),
        shorts_metadata_boost: shorts,
        pinned_comment: pinnedComment,
        paid_promotion: paidPromotion,
        altered_content: alteredContent,
        narration_project_id: narrationProjectId,
        story_id: storyId,
      });
      setUploadResult(data);
      mark('upload', 'done');
      mark('after', 'done');
      mark('handoff', 'done');
      setActive('after');
      toast.success('Uploaded unlisted. Finish the Studio remainders before going public.');
      if (Array.isArray(data.warnings)) data.warnings.forEach((warning) => toast.warning(warning));
    } catch (err) {
      if (err?.response?.status === 401) {
        mark('connect', 'needs_you');
        setNote('YouTube login expired. Reconnect. This draft is still here.');
        refreshConnection().catch(() => {});
      }
      mark('upload', 'failed');
      toast.error(formatApiError(err, 'Upload failed'));
    } finally {
      setRunning(false);
    }
  };

  const onPickProject = (task) => {
    const url = taskVideoUrl(task);
    if (!url) {
      toast.message('That project has no exported video yet.');
      return;
    }
    setVideoUrl(url);
    setTitle(task.title || '');
    setLanguage(task.language || language || 'en');
    setShorts(isShortsTask(task));
    setFromTwinnee(true);
    setNarrationProjectId(task.kind === 'narration' ? task.id : null);
    setStoryId(task.kind === 'story' ? task.id : null);
    setTranscript('');
    mark('video', 'done');
  };

  const onUploadFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setRunning(true);
    try {
      const url = String(await uploadFileViaPresignedUrl(file) || '').trim();
      if (!url.startsWith('http')) throw new Error('Upload finished without a public link');
      setVideoUrl(url);
      setFromTwinnee(false);
      setTranscript('');
      mark('video', 'done');
      toast.success('Video ready.');
    } catch (err) {
      toast.error(formatApiError(err, 'Could not upload the video'));
    } finally {
      setRunning(false);
    }
  };

  const startConnect = async () => {
    try {
      const { data } = await api.post('/social-publish/youtube/oauth/begin', {
        return_path: '/social-publish/auto',
      });
      if (data?.authorization_url) {
        window.location.href = data.authorization_url;
        return;
      }
      toast.error('Could not start YouTube login');
    } catch (err) {
      toast.error(formatApiError(err, 'Could not start YouTube login'));
    }
  };

  const publishDisabled = running || !legalReady || !videoUrl || !title.trim() || blocking.length > 0;

  return (
    <div className="px-3 sm:px-8 py-6 font-jakarta text-white" data-testid="auto-seo-publish">
      <div className="flex flex-col lg:flex-row gap-6">
        <aside className="lg:w-80 shrink-0 space-y-3">
          <div>
            <h1 className="text-2xl font-extrabold">Automate All</h1>
            <p className="text-sm text-[#8b95a7] mt-1">SEO + publish. Legal answers stay yours.</p>
          </div>
          <ol className="space-y-1">
            {STEPS.map((step) => {
              const status = statuses[step.id] || 'queued';
              const selected = active === step.id;
              return (
                <li key={step.id}>
                  <button
                    type="button"
                    onClick={() => setActive(step.id)}
                    className={`w-full flex items-center gap-3 rounded-2xl px-3 py-2.5 text-left text-sm cursor-pointer ${
                      selected ? 'bg-white/10' : 'hover:bg-white/5'
                    } ${status === 'done' ? 'text-[#d7f8ec]' : 'text-white'}`}
                  >
                    <span className={`flex h-6 w-6 items-center justify-center rounded-full ${status === 'done' ? 'bg-[#34d399]' : 'bg-black/30'}`}>
                      <StepIcon status={status} />
                    </span>
                    <span className="min-w-0">
                      <span className="block">{step.label}</span>
                      <span className="block text-[11px] uppercase tracking-wide text-[#6d7788]">{status.replace('_', ' ')}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
          <div className="flex flex-col gap-2 pt-2">
            <button
              type="button"
              disabled={running}
              onClick={() => resume()}
              className="rounded-full bg-gradient-to-r from-[#c4b5fd] via-[#a78bfa] to-[#67e8f9] text-[#080d14] font-bold py-2.5 cursor-pointer disabled:opacity-50"
              data-testid="auto-resume"
            >
              {running ? 'Working…' : 'Start / Resume'}
            </button>
            <button
              type="button"
              disabled={running}
              onClick={() => resume({ fromPackage: true })}
              className="rounded-full border border-white/15 py-2.5 text-sm cursor-pointer disabled:opacity-50"
            >
              Regenerate package
            </button>
            <button
              type="button"
              disabled={publishDisabled}
              onClick={publish}
              className="rounded-full bg-[#34d399] text-[#06211b] font-bold py-2.5 cursor-pointer disabled:opacity-40"
              data-testid="auto-publish"
            >
              {publishPublic ? 'Publish public now' : 'Publish unlisted'}
            </button>
          </div>
          {note ? <p className="text-xs text-[#d6ff4a]">{note}</p> : null}
        </aside>

        <section className="flex-1 min-w-0 space-y-4">
          {active === 'connect' ? (
            <Panel title="Connect YouTube">
              <p className="text-sm text-[#c5cedb]">
                {yt?.connected
                  ? `Connected${yt.channel_title ? ` as ${yt.channel_title}` : ''}.`
                  : 'YouTube is not connected.'}
              </p>
              {yt?.connected && yt?.scopes_ok === false ? (
                <p className="text-sm text-amber-100">Analytics permissions are missing. Reconnect and approve every scope.</p>
              ) : null}
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={startConnect} className="rounded-full border border-white/20 px-4 py-2 text-sm cursor-pointer">
                  {yt?.connected ? 'Reconnect' : 'Connect YouTube'}
                </button>
                <YouTubeDisconnectButton />
              </div>
            </Panel>
          ) : null}

          {active === 'video' ? (
            <Panel title="Choose the video">
              <div className="space-y-2 max-h-64 overflow-auto">
                {projects.filter((task) => taskVideoUrl(task)).map((task) => (
                  <button
                    key={`${task.kind}-${task.id}`}
                    type="button"
                    onClick={() => onPickProject(task)}
                    className="w-full text-left rounded-2xl bg-black/30 px-3 py-2 text-sm hover:bg-black/50 cursor-pointer"
                  >
                    {task.title || 'Untitled'} {isShortsTask(task) ? '· Short' : ''}
                  </button>
                ))}
              </div>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input type="file" accept="video/*" onChange={onUploadFile} />
                Upload a file
              </label>
              {videoUrl ? <p className="text-xs text-[#8b95a7] break-all">{videoUrl}</p> : null}
            </Panel>
          ) : null}

          {(active === 'package' || active === 'transcribe' || active === 'audit') ? (
            <Panel title="Package">
              <div className="flex flex-wrap gap-2">
                {variants.map((variant) => (
                  <button
                    key={variant.id}
                    type="button"
                    onClick={() => {
                      setTitle(variant.title);
                      runAudit({ title: variant.title }).catch(() => {});
                    }}
                    className={`rounded-full px-3 py-1 text-xs border cursor-pointer ${
                      title === variant.title ? 'border-[#34d399] text-[#34d399]' : 'border-white/15'
                    }`}
                  >
                    {variant.label}
                  </button>
                ))}
              </div>
              <label className="block text-xs text-[#8b95a7]">
                Title
                <input value={title} onChange={(event) => setTitle(event.target.value)} className="mt-1 w-full rounded-xl bg-black/30 px-3 py-2 text-sm text-white" />
              </label>
              <label className="block text-xs text-[#8b95a7]">
                Description
                <textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={8} className="mt-1 w-full rounded-xl bg-black/30 px-3 py-2 text-sm text-white" />
              </label>
              <label className="block text-xs text-[#8b95a7]">
                Tags
                <input
                  value={tags.join(', ')}
                  onChange={(event) => setTags(event.target.value.split(',').map((tag) => tag.trim()).filter(Boolean))}
                  className="mt-1 w-full rounded-xl bg-black/30 px-3 py-2 text-sm text-white"
                />
              </label>
              <button
                type="button"
                className="text-sm text-[#67e8f9] cursor-pointer"
                onClick={() => runAudit().then(() => toast.success('Audit updated')).catch((err) => toast.error(formatApiError(err, 'Audit failed')))}
              >
                Re-run audit
              </button>
              {audit ? <AuditList phases={(audit.phases || []).slice(0, 3)} /> : null}
              {audit && rankingUnlocked ? (
                <AuditList phases={(audit.phases || []).filter((phase) => phase.id === 'ranking')} />
              ) : null}
            </Panel>
          ) : null}

          {active === 'thumbnail' ? (
            <Panel title="Thumbnail">
              <div className="flex flex-wrap gap-3">
                {thumbOptions.map((url) => (
                  <button key={url} type="button" onClick={() => setThumbnailUrl(url)} className={`rounded-xl overflow-hidden border cursor-pointer ${thumbnailUrl === url ? 'border-[#34d399]' : 'border-white/10'}`}>
                    <img src={url} alt="" className="w-40 h-24 object-cover" />
                  </button>
                ))}
              </div>
              <label className="block text-xs text-[#8b95a7]">
                Overlay text (3 words max, do not repeat the title)
                <input value={thumbnailText} onChange={(event) => setThumbnailText(event.target.value)} className="mt-1 w-full rounded-xl bg-black/30 px-3 py-2 text-sm text-white" />
              </label>
            </Panel>
          ) : null}

          {active === 'legal' ? (
            <Panel title="Compliance">
              <p className="text-sm text-amber-100">We cannot legally choose these for you.</p>
              {fromTwinnee ? <p className="text-xs text-[#8b95a7]">This looks like a Twinnee project, so altered or synthetic content is likely yes.</p> : null}
              <YesNo label="Made for kids?" value={madeForKids} onChange={setMadeForKids} />
              <YesNo label="Paid promotion?" value={paidPromotion} onChange={setPaidPromotion} />
              <YesNo label="Altered or synthetic AI?" value={alteredContent} onChange={setAlteredContent} />
              <label className="block text-xs text-[#8b95a7]">
                Playlist
                <select value={playlistId} onChange={(event) => setPlaylistId(event.target.value)} className="mt-1 w-full rounded-xl bg-black/30 px-3 py-2 text-sm text-white">
                  <option value="">None</option>
                  {playlists.map((list) => (
                    <option key={list.id} value={list.id}>{list.title}</option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={hasSrt} onChange={(event) => setHasSrt(event.target.checked)} />
                I will upload a corrected SRT in YouTube Studio
              </label>
              <label className="block text-xs text-[#8b95a7]">
                Pinned comment draft
                <textarea value={pinnedComment} onChange={(event) => setPinnedComment(event.target.value)} rows={3} className="mt-1 w-full rounded-xl bg-black/30 px-3 py-2 text-sm text-white" />
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={publishPublic} onChange={(event) => setPublishPublic(event.target.checked)} />
                Publish public now
              </label>
            </Panel>
          ) : null}

          {(active === 'upload' || active === 'after' || active === 'handoff') ? (
            <Panel title={uploadResult ? 'Uploaded' : 'Upload'}>
              {uploadResult?.url ? (
                <a className="text-[#34d399] underline break-all" href={uploadResult.url} target="_blank" rel="noreferrer">{uploadResult.url}</a>
              ) : (
                <p className="text-sm text-[#8b95a7]">Publish stays unlisted until you confirm the legal toggles.</p>
              )}
              <ul className="text-sm text-[#c5cedb] space-y-2 list-disc pl-5">
                <li>Wait for YouTube Checks before flipping the video to public.</li>
                <li>Add an end screen and 1–2 info cards in Studio.</li>
                <li>Pin the drafted comment when the video goes live. This app cannot pin it.</li>
              </ul>
              {pinnedComment ? <p className="text-xs text-[#8b95a7]">Draft: {pinnedComment}</p> : null}
              <button
                type="button"
                onClick={() => {
                  if (!rankingUnlocked) {
                    navigate('/pricing?unlock=analytics');
                    return;
                  }
                  const videoId = youtubeVideoId(uploadResult);
                  navigate(videoId ? `/channel/advanced?video=${videoId}` : '/channel/advanced');
                }}
                className="rounded-full bg-amber-300 text-[#1a1204] font-bold px-4 py-2 cursor-pointer"
                data-testid="watch-ranking-button"
              >
                {rankingUnlocked ? 'Watch ranking (24h)' : 'Unlock Advanced Analytics'}
              </button>
            </Panel>
          ) : null}
        </section>
      </div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="rounded-[22px] border border-white/10 bg-[#121722] p-5 space-y-4">
      <h2 className="text-lg font-bold">{title}</h2>
      {children}
    </div>
  );
}

function YesNo({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span>{label}</span>
      <span className="flex gap-2">
        <button type="button" onClick={() => onChange(true)} className={`rounded-full px-3 py-1 cursor-pointer ${value === true ? 'bg-[#34d399] text-[#06211b]' : 'bg-black/30'}`}>Yes</button>
        <button type="button" onClick={() => onChange(false)} className={`rounded-full px-3 py-1 cursor-pointer ${value === false ? 'bg-[#34d399] text-[#06211b]' : 'bg-black/30'}`}>No</button>
      </span>
    </div>
  );
}

function AuditList({ phases }) {
  const steps = phases.flatMap((phase) => phase.steps || []);
  if (!steps.length) return null;
  return (
    <ul className="space-y-1 text-xs">
      {steps.map((step) => (
        <li key={step.id} className="flex justify-between gap-3">
          <span>{step.label}</span>
          <span className={step.status === 'pass' ? 'text-[#34d399]' : 'text-[#d6ff4a]'}>{step.status}</span>
        </li>
      ))}
    </ul>
  );
}
